// SQLite parseable-body fetcher (Slice 17 dispatch arm).
//
// Per the slice, SQLite exposes:
//   - views       → `sqlite_master WHERE type='view'`
//   - tables      → `sqlite_master WHERE type='table'`. Default + check
//                   constraints aren't surfaced as discrete catalog entries
//                   by SQLite — they live inline in the CREATE TABLE text —
//                   so the listing returns the raw CREATE TABLE body under
//                   the synthetic `check-constraint` kind keyed by the
//                   table's name. Slice 21's index-builder splits that
//                   text into individual constraint entries via the
//                   resolver-side parser.
//
// SQLite has no stored functions or procedures, so the fetcher never returns
// `inline-tvf` / `scalar-function` rows.

import type {
  ObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../../types';

import {
  asString,
  escapeSqlLiteral,
  fetchListingRows,
  fetchRows,
  makeListing,
  type NavListingFetchOptions,
} from './queryHelper';

function listing(
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return makeListing('sqlite', objectKind, schema, name, bodyText);
}

const VIEWS_SQL = `SELECT name AS name, sql AS body FROM sqlite_master
WHERE type = 'view' AND name NOT LIKE 'sqlite_%'`;

// SQLite stores the original CREATE TABLE text including any inline DEFAULT
// or CHECK clauses. Slice 21 will tokenize the text to find the individual
// constraints; Slice 17 just hands the table's full DDL down under
// `check-constraint` so the index-builder has source bytes to scan.
const TABLES_SQL = `SELECT name AS name, sql AS body FROM sqlite_master
WHERE type = 'table' AND name NOT LIKE 'sqlite_%' AND sql IS NOT NULL`;

// Page key: `sqlite_master.rowid`, the table's implicit unique row id.
// DIVERGENCE: SQLite has no STORED PROCEDURES; it does have triggers, but this index never fetches them (`sqliteObjectBodyFetcher.ts` selects `type = 'view'` and `type = 'table'` only) — so there is no
// `listSqliteProceduralObjects` sibling — the dispatcher in
// `objectBodyFetcher.ts` returns `[]` for it.
export async function listSqliteParseableObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const [viewRows, tableRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'sqlite',
      label: 'sqlite-views',
      sql: VIEWS_SQL,
      orderBy: 'rowid',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
    fetchListingRows(options, {
      engine: 'sqlite',
      label: 'sqlite-tables',
      sql: TABLES_SQL,
      orderBy: 'rowid',
      dialect: 'limit-offset',
      bodyColumns: ['body'],
    }),
  ]);

  const out: PersistedObjectListing[] = [];
  // SQLite has a single anonymous schema; report it as 'main' so the index's
  // schema-keyed lookups stay consistent across engines.
  const schema = 'main';

  for (const row of viewRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    out.push(listing('view', schema, name, body));
  }

  for (const row of tableRows) {
    const body = asString(row.body);
    const name = asString(row.name);
    if (!name || !body) continue;
    if (!/\b(DEFAULT|CHECK)\b/i.test(body)) continue;
    out.push(listing('check-constraint', schema, name, body));
  }

  return out;
}

export async function fetchSqliteObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  const n = escapeSqlLiteral(objectName.name);

  // DIVERGENCE (SQLite object-name case-sensitivity): SQLite object names are
  // case-insensitive (`CREATE VIEW Foo` and `CREATE VIEW IF NOT EXISTS foo`
  // name the same object), but `sqlite_master.name` compares BINARY unless
  // told otherwise — an unqualified `name = '<n>'` is case-EXACT. The DDL
  // invalidator re-verifies an object by this lookup after every DDL
  // statement; without `COLLATE NOCASE` a differently-cased re-verification
  // (e.g. the harmless `CREATE VIEW IF NOT EXISTS foo …` re-stating an
  // existing `Foo`) finds no row, is read as "object dropped", and the
  // refresh deletes the live object's reference records.
  if (objectKind === 'view') {
    const sql = `SELECT sql AS body FROM sqlite_master WHERE type = 'view' AND name = '${n}' COLLATE NOCASE`;
    const rows = await fetchRows(connectionId, database, sql, 'sqlite-view');
    return asString(rows[0]?.body) || null;
  }

  if (objectKind === 'check-constraint' || objectKind === 'default-constraint') {
    const sql = `SELECT sql AS body FROM sqlite_master WHERE type = 'table' AND name = '${n}' COLLATE NOCASE`;
    const rows = await fetchRows(connectionId, database, sql, 'sqlite-table-ddl');
    return asString(rows[0]?.body) || null;
  }

  return null;
}
